import { Box, paletteColors, Stack, Typography } from '@evaneos/b2b-ui';
import ArrowBackIcon from '@evaneos/b2b-ui/icons/ArrowBack';

import { TravelSuiteFooter } from './components/TravelSuiteFooter';
import { TravelSuiteHeader } from './components/TravelSuiteHeader';
import { DPO_EMAIL, homeBasename } from './constants/urls';

const SectionTitle = ({ id, children }: { id: string; children: string }) => (
    <Typography
        id={id}
        component="h2"
        variant="h4"
        fontWeight={700}
        sx={{ color: paletteColors.neutral[900], mt: 6, mb: 2, scrollMarginTop: '80px' }}
    >
        {children}
    </Typography>
);

const SubTitle = ({ children }: { children: string }) => (
    <Typography
        component="h3"
        variant="subtitle1"
        fontWeight={700}
        sx={{ color: paletteColors.neutral[900], mt: 3, mb: 1 }}
    >
        {children}
    </Typography>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="body1"
        sx={{ color: paletteColors.neutral[700], lineHeight: 1.8, mb: 1.5 }}
    >
        {children}
    </Typography>
);

const BulletList = ({ items }: { items: React.ReactNode[] }) => (
    <Box
        component="ul"
        sx={{ pl: 4, mb: 2, listStyleType: 'disc', '& li': { mb: 1, display: 'list-item' } }}
    >
        {items.map((item, i) => (
            <Typography
                key={i}
                component="li"
                variant="body1"
                sx={{ color: paletteColors.neutral[700], lineHeight: 1.8 }}
            >
                {item}
            </Typography>
        ))}
    </Box>
);

const PrivacyPolicySections = () => (
    <>
        <SectionTitle id="section-1">1. Parties and Scope</SectionTitle>
        <Paragraph>
            This privacy policy (the &quot;Privacy Policy&quot;) applies to the processing of
            personal data within the framework of the Partnership agreement between:
        </Paragraph>
        <BulletList
            items={[
                <>
                    <strong>EVANEOS SA</strong>, a public limited company registered in the Paris
                    Trade and Companies Register under number RCS 513 191 122 and whose registered
                    office is at 27 Rue de Mogador in Paris (75009) (herein after
                    &quot;Evaneos&quot;), acting as Data Controller within the meaning of the
                    European Data Protection Regulation of 27 April 2016 (hereinafter referred to as
                    the &quot;GDPR&quot;); and
                </>,
                <>
                    the <strong>local partner agency</strong> (herein after &quot;the Agency&quot;),
                    in its capacity as Data Controller for the performance of its own business
                    purposes.
                </>,
            ]}
        />
        <Paragraph>
            The Privacy Policy explains how Evaneos processes personal data relating to
            Agency&apos;s directors, contractors and employees in connection in the context of its
            relationship and Partnership agreement with the Agency, including in connection with the
            provision of Evaneos digital tools made available to the Agency, namely{' '}
            <strong>Travel Suite</strong> (including Request Manager, App Agent, Trip Planner, Offer
            Manager, Cash Manager, Insight, Agency Manager and Agency Insight), as well as{' '}
            <strong>Partner Connect</strong> (herein after, together, the &quot;Evaneos Digital
            Tools&quot;).
        </Paragraph>
        <Paragraph>
            The processing of personal data relating to travelers and their travel companions is
            governed by the provisions of the Partnership agreement and does not fall within the
            scope of this Privacy Policy.
        </Paragraph>
        <Paragraph>
            This policy is subject to the amended French Data Protection Act no. 78-17 of 6 January
            1978 (hereinafter referred to as the &quot;Data Protection Act&quot;) and the GDPR.
        </Paragraph>
        <Paragraph>
            It may change depending on the legal and regulatory context and the policy of the CNIL
            (French data protection authority).
        </Paragraph>

        <SectionTitle id="section-2">2. Processing of Personal Data</SectionTitle>
        <Paragraph>
            Evaneos act as Data Controller, within the meaning of the GDPR, for the operation and
            management of its network of Local Partner Agencies.
        </Paragraph>
        <SubTitle>Processing purposes</SubTitle>
        <Paragraph>
            Evaneos acts as the Data Controller within the meaning of Article 4 of the GDPR, for the
            following processing purposes:
        </Paragraph>
        <BulletList
            items={[
                'Provide Partner Travel Agencies with a suite of tools enabling them to manage their operations and centralize their communications with Travelers and prospects;',
                'Monitor the quality of services provided by the Agencies;',
                'Relationship management with Agencies;',
                'Train and inform Agencies through a centralized tool (Partner Connect and User Pilot).',
                'Evaluation of the use of tools made available to the Agency and improvement of Evaneos tools, services and functionalities, in particular through the use and training of artificial intelligence systems;',
                'Provide post-training assessment tools;',
                'Provide a discussion forum between Agencies and with Evaneos;',
                'Provide customer support (A-Care and C-Care) in handling customer service tickets.',
            ]}
        />
        <SubTitle>Categories of data collected and processed</SubTitle>
        <Paragraph>
            To fulfil these purposes, Evaneos collects and processes personal data relating to the
            Agency&apos;s directors, contractors and employees.
        </Paragraph>
        <Paragraph>The following categories of data are processed:</Paragraph>
        <BulletList
            items={[
                <>
                    <strong>Identification data</strong>, such as surname, first name, civility,
                    e-mail address, postal address and telephone number, as well as, where
                    applicable, date and place of birth, and photographs or other identification
                    elements;
                </>,
                <>
                    <strong>Professional information</strong> such as position, role and Agency
                    affiliation;
                </>,
                <>
                    <strong>Communication data</strong> such as the content of exchanges between
                    travelers and the Agency&apos;s directors or employees via the Evaneos Digital
                    Tools;
                </>,
                <>
                    <strong>Connection data</strong> such as IP addresses, logs, device identifiers,
                    login credentials, timestamps;
                </>,
                <>
                    <strong>Calendar data</strong>: Access to the data of the professional Google
                    Calendar of Agency&apos;s directors, contractors and employees as described in
                    Appendix 1.
                </>,
            ]}
        />
        <SubTitle>Evaneos&apos; obligations</SubTitle>
        <Paragraph>In this respect, Evaneos undertakes to:</Paragraph>
        <BulletList
            items={[
                'Collect and process personal data in a lawful, fair and transparent manner;',
                'Put in place clear, legible and accessible information in accordance with Articles 13 and 14 of the GDPR;',
                'Collect personal data for specified, explicit and legitimate purposes, and refrain from further processing them in a manner incompatible with those initial purposes;',
                'Only collect and process personal data that is adequate, relevant, accurate, kept up to date and limited to what is necessary for the purposes for which it is processed;',
                'Keep the personal data in a form allowing the identification of the data subject for a period not exceeding that necessary for the purposes for which they are processed;',
                'Implement the appropriate physical, logical, organizational and contractual security measures in order to preserve the security of personal data and in particular to prevent it from being distorted, damaged or communicated to unauthorized persons;',
                "Implement all necessary measures to respect the data subjects' rights of access, rectification, erasure, limitation, portability and opposition, when legally applicable;",
                'Implement the appropriate guarantees, in accordance with Article 46 of the GDPR, when setting up transfers of personal data outside the European Economic Area (alternatively EU-US Data Privacy Framework and Standard Contractual Clauses of the European Commission);',
                'Submit its subcontractors to the same obligations and regularly check compliance with these obligations by requirements in terms of security policy.',
            ]}
        />
        <Paragraph>
            According to Article 24 of the GDPR, Evaneos undertakes to implement the appropriate
            technical and organizational measures to ensure and be able to demonstrate that data
            processings are carried out in accordance with the provisions of the GDPR and national
            legislation.
        </Paragraph>
        <Paragraph>
            According to Article 37 of the GDPR, Evaneos has designated a Data Protection Officer.
        </Paragraph>

        <SectionTitle id="section-3">3. Cookies and Trackers</SectionTitle>
        <Paragraph>
            When an Agency&apos;s director or employee use the Evaneos Digital Tools, user
            information may be recorded by trackers or in &quot;Cookie&quot; files placed on its
            computer.
        </Paragraph>
        <Paragraph>These cookies and trackers may be:</Paragraph>
        <BulletList
            items={[
                <>
                    <strong>Technical and functional.</strong> In this case, they are strictly
                    necessary for the proper functioning of the Evaneos Digital Tools: these cookies
                    are deposited on user&apos;s computer without its consent and cannot be
                    deactivated because this would disrupt the proper functioning of the Service.
                </>,
                <>
                    <strong>Analytical:</strong> this enables Evaneos to produce statistical
                    analyses of visitor numbers and browsing patterns, thus enabling Evaneos to
                    improve the Digital Tools.
                </>,
            ]}
        />

        <SectionTitle id="section-4">4. Rights to Access, Rectify and Delete</SectionTitle>
        <Paragraph>
            Any individual whose personal data has been collected has the right to access their
            personal data and to request the rectification of inaccurate or incomplete data. In the
            cases provided for under applicable regulations, individuals may also object to the
            processing of their personal data, request its erasure or restrict its processing.
        </Paragraph>
        <Paragraph>
            To exercise any of these rights, the person must contact the Evaneos DPO at{' '}
            <Box
                component="a"
                href={`mailto:${DPO_EMAIL}`}
                sx={{ color: paletteColors.green[700] }}
            >
                {DPO_EMAIL}
            </Box>{' '}
            or by post to the following address: 27 Rue de Mogador, 75009, Paris, France.
        </Paragraph>
        <Paragraph>
            If, for security reasons, Evaneos is unable to verify the identity of the requesting
            individual with sufficient certainty, a copy of an identity document may be required to
            process the request.
        </Paragraph>
        <Paragraph>
            A response will be provided within a maximum period of one (1) month from receipt of the
            request, unless this period is extended in accordance with the conditions set out in the
            GDPR.
        </Paragraph>
        <Paragraph>
            Evaneos may, where permitted by applicable regulations, refuse to comply with certain
            requests relating to these rights (in particular the right to erasure), for legitimate
            reasons such as the need to defend legal claims or to comply with a legal obligation to
            retain certain data.
        </Paragraph>
        <Paragraph>
            Any individual also has the right to lodge a complaint with the Commission Nationale
            Informatique et Libertés (CNIL).
        </Paragraph>

        <SectionTitle id="section-5">5. Update and Modification of the Privacy Policy</SectionTitle>
        <Paragraph>
            Evaneos reserves the right to update or modify this Privacy Policy at any time. In the
            event of a material change, data subjects will be informed by appropriate means,
            including, where relevant, by email, in order to allow a reasonable period to exercise
            their rights under applicable legislation.
        </Paragraph>
        <Paragraph>
            Data subjects are nevertheless encouraged to consult this Privacy Policy on a regular
            basis in order to remain informed of how personal data is processed by Evaneos.
        </Paragraph>
    </>
);

const GoogleCalendarAppendix = () => (
    <Box sx={{ mt: 8, pt: 6, borderTop: `2px solid ${paletteColors.neutral[200]}` }}>
        <Typography
            id="appendix-1"
            variant="titleMoretXL"
            component="h2"
            sx={{
                color: paletteColors.neutral[900],
                mb: 3,
                fontSize: { xs: '2.8rem', md: '3.6rem' },
                scrollMarginTop: '80px',
            }}
        >
            Appendix 1 - Google Calendar data processing
        </Typography>

        <Paragraph>
            To optimize the management of traveler requests, Evaneos could process Google Calendar
            data relating to the Agency&apos;s directors, contractors and employees. When an
            Agency&apos;s employee, contractor or director chooses to synchronize their Google
            Calendar with Evaneos tools, the following rules apply:
        </Paragraph>
        <SubTitle>Google Calendar data:</SubTitle>
        <BulletList
            items={[
                <>
                    <strong>Data accessed (Read):</strong> Evaneos accesses Google Calendar data
                    strictly to identify availability slots, the list of events, and the list of
                    calendars.
                </>,
                <>
                    <strong>Data modified (Write):</strong> Evaneos is authorized to create, modify,
                    and cancel events within the Google Calendar solely for actions initiated
                    through the Evaneos Digital Tools (e.g., scheduling or updating traveler
                    appointments).
                </>,
                <>
                    <strong>Data retained by Evaneos:</strong> Evaneos only saves and stores the
                    authentication tokens required for the connection (encrypted) and the unique IDs
                    of events specifically created by Evaneos.
                </>,
                <>
                    <strong>Strict limitation:</strong> No other data from the user&apos;s Google
                    Calendar is collected, processed, or stored by Evaneos.
                </>,
            ]}
        />
        <SubTitle>Purposes of use:</SubTitle>
        <Paragraph>Google Calendar data is used strictly to:</Paragraph>
        <BulletList
            items={[
                'Display availability of Agency staff',
                'Schedule meetings between Travelers and Agents',
                'Create, modify, and cancel appointments',
            ]}
        />
        <SubTitle>Limited use compliance:</SubTitle>
        <Paragraph>
            In accordance with the Google API Services User Data Policy (Limited Use):
        </Paragraph>
        <BulletList
            items={[
                'Data is not used for advertising',
                'Data is not used for profiling',
                'Data is not shared or sold to third parties',
                'Data is used only for user-facing functionalities',
            ]}
        />
        <SubTitle>Data sharing:</SubTitle>
        <BulletList
            items={[
                'No third-party tools access Google Calendar data',
                'Data is used internally only',
            ]}
        />
        <SubTitle>Storage and security:</SubTitle>
        <BulletList
            items={[
                'Data is hosted on Google Cloud',
                'OAuth credentials are encrypted',
                'Access is restricted to authorized systems only',
                'Retention: Until the user disconnects their Google account or upon deletion of the user account',
            ]}
        />
        <SubTitle>User rights and revocation:</SubTitle>
        <BulletList
            items={[
                <>
                    Users may revoke access at any time by disconnecting their Google account within
                    the Evaneos Digital Tools.
                </>,
                <>
                    Users may also exercise their rights or request deletion via{' '}
                    <Box
                        component="a"
                        href={`mailto:${DPO_EMAIL}`}
                        sx={{ color: paletteColors.green[700] }}
                    >
                        {DPO_EMAIL}
                    </Box>
                </>,
            ]}
        />
    </Box>
);

export const PrivacyPolicyPage = (): JSX.Element => (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <TravelSuiteHeader />
        <Box
            component="main"
            sx={{
                flex: 1,
                backgroundColor: paletteColors.neutral[0],
                px: { xs: 2, md: 8 },
                py: { xs: 6, md: 10 },
            }}
        >
            <Box sx={{ maxWidth: '860px', mx: 'auto' }}>
                <Box
                    component="a"
                    href={homeBasename}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: paletteColors.neutral[500],
                        textDecoration: 'none',
                        mb: 3,
                        fontSize: '1.4rem',
                        '&:hover': { color: paletteColors.green[700] },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: '1.6rem' }} />
                    Travel Suite
                </Box>
                <Typography
                    variant="titleMoretXL"
                    component="h1"
                    sx={{ color: paletteColors.neutral[900], mb: 1 }}
                >
                    Local Partner Agencies Privacy Policy
                </Typography>
                <Typography variant="body2" sx={{ color: paletteColors.neutral[400], mb: 4 }}>
                    Last updated: May 20, 2026
                </Typography>
                <Stack spacing={0.5} mb={5}>
                    {[
                        { label: '1. Parties and scope', href: '#section-1' },
                        { label: '2. Processing of personal data', href: '#section-2' },
                        { label: '3. Cookies and trackers', href: '#section-3' },
                        { label: '4. Rights to access, rectify and delete', href: '#section-4' },
                        {
                            label: '5. Update and modification of the Privacy Policy',
                            href: '#section-5',
                        },
                        {
                            label: 'Appendix 1 - Google Calendar data processing',
                            href: '#appendix-1',
                        },
                    ].map(({ label, href }) => (
                        <Box
                            key={label}
                            component="a"
                            href={href}
                            sx={{
                                color: paletteColors.green[700],
                                fontSize: '1.4rem',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            {label}
                        </Box>
                    ))}
                </Stack>
                <PrivacyPolicySections />
                <GoogleCalendarAppendix />
            </Box>
        </Box>
        <TravelSuiteFooter />
    </Box>
);
